import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Button, Form, Image, Progress, Tooltip, Upload } from "antd";
import {
  CheckCircleFilled,
  DeleteOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  addUpdateDocuments,
  getDocuments,
} from "../../../../api/request/corporate.js";
import { handleApiError } from "../../../../utils/handleApiError.js";
import {
  CORPORATE_DOCUMENT_FIELDS,
  getCorporateDocumentFromResponse,
  mapCorporateDocumentFromApi,
} from "../../../../utils/corporateDocuments.js";
import { useTranslation } from "../../../../utils/onboardingI18n";

const ACCEPTED_FORMATS = ".jpg,.jpeg,.png,.pdf,.doc,.docx";

const REQUIRED_CORPORATE_DOCUMENT_FIELDS = CORPORATE_DOCUMENT_FIELDS.filter(
  (doc) => doc.required !== false
);

const createEmptyDocumentState = () =>
  CORPORATE_DOCUMENT_FIELDS.reduce((acc, { key }) => {
    acc[key] = null;
    return acc;
  }, {});

const UploadDocuments = ({
  onNext,
  onPrevious,
  onApplicationSubmitted,
  id,
  isSubmitted,
}) => {
  const { t } = useTranslation("onBoarding");
  const [form] = Form.useForm();
  const [apiData, setApiData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [documentFiles, setDocumentFiles] = useState(createEmptyDocumentState);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  const isDisabled = isSubmitted;

  const uploadedCount = useMemo(
    () =>
      CORPORATE_DOCUMENT_FIELDS.filter(({ key }) => documentFiles[key]).length,
    [documentFiles]
  );

  const requiredUploadedCount = useMemo(
    () =>
      REQUIRED_CORPORATE_DOCUMENT_FIELDS.filter(({ key }) => documentFiles[key])
        .length,
    [documentFiles]
  );

  const requiredDocumentCount = REQUIRED_CORPORATE_DOCUMENT_FIELDS.length;

  const progressPercent = Math.round(
    (requiredUploadedCount / requiredDocumentCount) * 100
  );

  const getDocumentsData = async () => {
    setIsLoading(true);
    try {
      const { data } = await getDocuments(id);
      if (data?.success) {
        setApiData(data?.data);
        const corporateDocuments = data?.data?.corporateDocuments || {};
        const nextFiles = createEmptyDocumentState();
        const formValues = {};

        CORPORATE_DOCUMENT_FIELDS.forEach(({ key, payloadKey }) => {
          const doc = getCorporateDocumentFromResponse(
            corporateDocuments,
            key,
            payloadKey
          );
          const mapped = mapCorporateDocumentFromApi(doc);

          if (mapped) {
            nextFiles[key] = mapped;
            formValues[key] = mapped;
          }
        });

        setDocumentFiles(nextFiles);
        form.setFieldsValue(formValues);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) getDocumentsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getFileUrl = (file) => {
    if (!file) return "";
    if (file?.fileUrlPath) return file.fileUrlPath;
    if (file?.originFileObj) return URL.createObjectURL(file.originFileObj);
    return "";
  };

  const isImageFile = (file) => {
    const name = file?.originFileObj?.name || file?.fileName || "";
    const type = file?.originFileObj?.type || "";
    return type.startsWith("image/") || /\.(jpg|jpeg|png)$/i.test(name);
  };

  const handlePreview = (file) => {
    const url = getFileUrl(file);
    if (!url) return;

    if (isImageFile(file)) {
      setPreviewImage(url);
      setPreviewOpen(true);
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDocumentChange = (key, { fileList }) => {
    if (fileList.length > 0 && fileList[0].originFileObj) {
      const file = fileList[0];
      const fieldConfig = CORPORATE_DOCUMENT_FIELDS.find(
        (doc) => doc.key === key
      );
      const mapped = {
        id: documentFiles[key]?.id || Date.now(),
        file,
        fileUrlPath: getFileUrl(file),
        fileName: file.originFileObj?.name || file.name,
        status: "PROCESSING",
        documentType: fieldConfig?.documentType,
        documentSubType: fieldConfig?.documentSubType,
      };
      setDocumentFiles((prev) => ({ ...prev, [key]: mapped }));
      form.setFieldsValue({ [key]: mapped });
    }
  };

  const handleDeleteDocument = (key) => {
    if (isDisabled) return;
    setDocumentFiles((prev) => ({ ...prev, [key]: null }));
    form.setFieldsValue({ [key]: null });
  };

  const appendDocumentToFormData = (
    formData,
    payloadKey,
    fieldValue,
    existingDoc,
    fieldConfig
  ) => {
    if (!fieldValue?.file && !fieldValue?.fileUrlPath) return;

    const documentId = fieldValue?.id || existingDoc?.id;
    if (documentId) {
      formData.append(`${payloadKey}.Id`, documentId);
    }

    const fileObj = fieldValue?.file?.originFileObj || fieldValue?.file;

    if (fileObj instanceof Blob) {
      formData.append(
        `${payloadKey}.File`,
        fileObj,
        fileObj?.name || `${payloadKey}.pdf`
      );
    } else if (fieldValue?.fileUrlPath) {
      formData.append(`${payloadKey}.FileUrlPath`, fieldValue.fileUrlPath);
    }

    const documentType =
      fieldValue?.documentType ??
      existingDoc?.documentType ??
      fieldConfig?.documentType;
    const documentSubType =
      fieldValue?.documentSubType ??
      existingDoc?.documentSubType ??
      fieldConfig?.documentSubType;

    if (documentType != null) {
      formData.append(`${payloadKey}.DocumentType`, documentType);
    }
    if (documentSubType != null) {
      formData.append(`${payloadKey}.DocumentSubType`, documentSubType);
    }
  };

  const handleSubmit = async (values) => {
    if (isDisabled) return;
    setIsSubmitLoading(true);
    try {
      const formData = new FormData();
      const corporateDocuments = apiData?.corporateDocuments || {};

      CORPORATE_DOCUMENT_FIELDS.forEach(
        ({ key, payloadKey, ...fieldConfig }) => {
          const existingDoc = getCorporateDocumentFromResponse(
            corporateDocuments,
            key,
            payloadKey
          );

          appendDocumentToFormData(
            formData,
            payloadKey,
            values[key],
            existingDoc,
            fieldConfig
          );
        }
      );

      const res = await addUpdateDocuments(formData);

      if (res?.data?.success) {
        toast.success(res?.data?.message);
        onApplicationSubmitted?.(id);
        onNext();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const renderDocumentRow = (doc, index) => {
    const { key, titleKey, instructionKey, accent, required = true } = doc;
    const file = documentFiles[key];
    const isUploaded = Boolean(file);

    return (
      <div key={key}>
        <Form.Item
          name={key}
          hidden
          rules={
            required
              ? [
                  {
                    required: true,
                    message: t("Please upload {{document}}", {
                      document: t(titleKey),
                    }),
                  },
                ]
              : []
          }
        />
        <div
          className={`flex items-center gap-3 px-3 py-2.5 sm:px-4 sm:py-3 transition-colors ${
            isUploaded ? "bg-emerald-50/60" : "bg-white hover:bg-slate-50/80"
          }`}
        >
          <div
            className="flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-semibold"
            style={{ backgroundColor: accent }}
          >
            {index + 1}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium text-slate-800 truncate">
                {t(titleKey)}
                {!required && (
                  <span className="text-slate-400 font-normal">
                    {" "}
                    ({t("Optional")})
                  </span>
                )}
              </p>
              <Tooltip title={t(instructionKey)}>
                <InfoCircleOutlined className="text-slate-400 text-xs flex-shrink-0 cursor-help" />
              </Tooltip>
            </div>
            {isUploaded && (
              <p className="text-xs text-slate-500 truncate mt-0.5 flex items-center gap-1">
                <CheckCircleFilled className="text-emerald-500 text-[10px]" />
                {file?.fileName || t("Uploaded")}
              </p>
            )}
          </div>

          <div className="flex-shrink-0 flex items-center gap-1.5">
            {isUploaded ? (
              <>
                <Tooltip title={t("Preview")}>
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => handlePreview(file)}
                    className="!text-primary"
                    disabled={false}
                  />
                </Tooltip>
                {!isDisabled && (
                  <Tooltip title={t("Remove")}>
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteDocument(key)}
                    />
                  </Tooltip>
                )}
              </>
            ) : isDisabled ? (
              <span className="text-xs text-slate-400 px-2">
                {t("Not uploaded")}
              </span>
            ) : (
              <Upload
                accept={ACCEPTED_FORMATS}
                showUploadList={false}
                beforeUpload={() => false}
                onChange={(info) => handleDocumentChange(key, info)}
                disabled={isDisabled}
              >
                <Button
                  type="default"
                  size="small"
                  icon={<UploadOutlined />}
                  className="!text-xs !h-8"
                >
                  {t("Upload")}
                </Button>
              </Upload>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto mb-4">
      <div className="mb-4">
        <div className="flex items-center justify-between gap-3 mb-2">
          <h2 className="text-xl font-semibold text-slate-800">
            {t("Upload Documents")}
          </h2>
          <span className="text-sm font-medium text-slate-500 whitespace-nowrap">
            {requiredUploadedCount}/{requiredDocumentCount} {t("uploaded")}
            {uploadedCount > requiredUploadedCount && (
              <span className="text-slate-400">
                {" "}
                (+{uploadedCount - requiredUploadedCount} {t("optional")})
              </span>
            )}
          </span>
        </div>

        <Progress
          percent={progressPercent}
          size="small"
          showInfo={false}
          strokeColor={{ from: "#7367F0", to: "#28C76F" }}
          className="!mb-3"
        />

        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 leading-relaxed">
          <span className="font-semibold">{t("Please ensure that:")} </span>
          {t(
            "These documents contain the following details: Company Name, Company Number, Registered Address, List of Directors and Beneficiary Owners"
          )}
        </p>

        <p className="text-xs text-red-500 mt-2">
          {t(
            "File Types: .jpg, .pdf, .png, .doc and .docx; max file size 12 Mb."
          )}
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        onFinish={handleSubmit}
      >
        <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100 shadow-sm mb-5">
          {CORPORATE_DOCUMENT_FIELDS.map((doc, index) =>
            renderDocumentRow(doc, index)
          )}
        </div>

        <div className="flex justify-between items-center">
          <Button onClick={onPrevious}>{t("Previous")}</Button>

          {isDisabled ? (
            <Button type="primary" onClick={onNext}>
              {t("Next")}
            </Button>
          ) : (
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitLoading || isLoading}
              disabled={
                requiredUploadedCount < REQUIRED_CORPORATE_DOCUMENT_FIELDS.length
              }
            >
              {isSubmitLoading ? t("Saving...") : t("Next")}
            </Button>
          )}
        </div>
      </Form>

      {previewImage ? (
        <Image
          wrapperStyle={{ display: "none" }}
          src={previewImage}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => {
              setPreviewOpen(visible);
              if (!visible) setPreviewImage("");
            },
          }}
        />
      ) : null}
    </div>
  );
};

export default UploadDocuments;
